import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Heart, ChevronDown, Search } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { useSaved } from "@/context/SavedContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "For Sale", to: "/properties/for-sale" },
  { label: "To Let", to: "/properties/to-let" },
  { label: "Featured", to: "/featured" },
  { label: "About", to: "/about" },
  { label: "Partner With Us", to: "/partner" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { saved } = useSaved();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-border shadow-sm" : "bg-white border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-6">
        <Logo className="text-2xl" />

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="text-sm font-semibold text-[#111827]/80 hover:text-[#C81D35] transition-colors"
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/saved" className="relative p-2 rounded-full hover:bg-muted transition-colors" data-testid="saved-icon">
            <Heart className="w-5 h-5 text-[#111827]" />
            {saved.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#C81D35] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {saved.length}
              </span>
            )}
          </Link>
          <Link to="/properties" className="hidden sm:block">
            <Button className="rounded-full bg-[#C81D35] hover:bg-[#a5162b] text-white px-5 gap-2" data-testid="find-property-btn">
              <Search className="w-4 h-4" /> Find a Property
            </Button>
          </Link>
          <button
            className="lg:hidden p-2 rounded-md hover:bg-muted"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-white animate-fade-up" data-testid="mobile-menu">
          <nav className="px-5 py-4 flex flex-col">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="py-3 text-base font-semibold text-[#111827] border-b border-border/60 last:border-0"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </Link>
            ))}
            <Link to="/properties" className="mt-4">
              <Button className="w-full rounded-full bg-[#C81D35] hover:bg-[#a5162b]">Find a Property</Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
